"use client";

import { useCallback, useEffect, useState } from "react";

type Theme = "light" | "dark";

function readTheme(): Theme {
  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeToggle() {
  // `mounted` distinguishes server / pre-hydration from post-hydration.
  // The previous implementation used `useSyncExternalStore` with a
  // hardcoded "dark" server snapshot, which produced a text/attribute
  // mismatch with the client snapshot for any user on a light-mode
  // system. `suppressHydrationWarning` was tried first but only silences
  // the dev-mode console warning — React 18+ still throws #418 in
  // production for the same mismatch. See
  // tasks/hydration-investigation-2026-05-07.md +
  // tasks/lessons.md → "suppressHydrationWarning does not prevent React
  // #418 in production builds" for why this pattern is correct.
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<Theme>("dark");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Deliberate post-hydration sync, not a cascading render. `mounted`/`theme` MUST start at the server-rendered values and flip after hydration; reading localStorage during render would reintroduce the React #418 mismatch this component was rewritten to fix (see comment above).
    setMounted(true);
    setTheme(readTheme());
    const onStorage = () => setTheme(readTheme());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Mirror the theme onto <html> whenever it changes after mount.
  useEffect(() => {
    if (!mounted) return;
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme, mounted]);

  const toggle = useCallback(() => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    localStorage.setItem("theme", next);
    setTheme(next);
    // StorageEvent only fires across tabs, so notify same-tab listeners.
    window.dispatchEvent(new StorageEvent("storage", { key: "theme", newValue: next }));
  }, [theme]);

  // Render a placeholder during SSR + initial client render so server
  // output matches client first paint (clean hydration). After useEffect
  // runs, `mounted` flips and the placeholder is replaced by the real
  // toggle in a normal post-hydration update — not a hydration step,
  // so React #418 cannot fire here.
  //
  // The placeholder reserves the same w-7 h-7 footprint the button
  // occupies once mounted, so the sidebar layout doesn't reflow on
  // hydration.
  if (!mounted) {
    return <span aria-hidden="true" className="inline-block w-7 h-7 align-middle" />;
  }

  const dark = theme === "dark";
  return (
    <button
      onClick={toggle}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      className="text-text-secondary hover:text-text-primary transition-colors px-1.5 py-1 rounded cursor-pointer text-sm"
    >
      {dark ? "☀️" : "🌙"}
    </button>
  );
}
